import React from "react";

import Breadcrumbs from "../component/breadcrumbs";
import ContentArea from "../component/ContentArea";
import CustomHelmet from "../component/CustomHelmet";
import Intro from "../component/Intro";
import BaseContainer from "../component/layout/base-container";
import { SlideDown } from "../component/SlideDown";
import Citation from "../lib/citation";

export default function About() {
  const klappen_texte = [
    {
      title: "Was ist FormATool?",
      text: (
        <>
          <p>
            FormATool steht für "Formatives Assessment Tool" mit Betonung auf
            den formativen Verwendungszweck. Das angebotene Tool soll
            Lehrpersonen helfen, formative Rückmeldungen von Schüler*innen und
            Beobachter*innen mit eigenen Einschätzungen der kognitiven
            Aktivierung im Unterricht abzugleichen.
          </p>
          <p>
            Wollen Lehrpersonen ihren eigenen Unterricht weiterentwickeln, sind
            Hinweise zum Ist-Zustand wesentlich. Im Unterricht finden solche
            Bestandsaufnahmen meist in Form von offiziellen Unterrichtsbesuchen
            durch die Schulleitung, Schulpflege oder im Rahmen der externen
            Evaluation statt. Bei solchen Gelegenheiten erhalten Lehrpersonen
            beobachtungsbasierte Rückmeldungen zur Qualität des eigenen
            Unterrichts, die oft einen summativen bzw. evaluierenden Charakter
            aufweisen.
          </p>
          <p>
            Mit Hilfe von FormATool soll Lehrpersonen ermöglicht werden, in
            Ergänzung zu solchen summativen bzw. evaluierenden Bestandsaufnahmen
            von Unterricht eine selbstständig steuerbare Rückmeldung zum
            Unterricht einzuholen. Die eigens angestrebte, evidenzbasierte
            Unterrichtsentwicklung steht dabei im Vordergrund. Lehrpersonen
            können entscheiden, wann und wie oft sie Rückmeldungen zu ihrem
            unterrichtlichen Handeln einholen wollen. Durch die Aus- und
            Weiterbildung werden Lehrpersonen darin geschult, das eigene Handeln
            im Unterricht zu reflektieren und somit Weiterentwicklungspunkte zu
            identifizieren. Doch eine durch und durch realistische Einschätzung
            des eigenen Unterrichtshandelns würde voraussetzen, dass während des
            Unterrichtens eine Meta-Perspektive eingenommen wird, von der aus
            Unterrichtsprozesse bilanziert werden. Da das Unterrichten
            allerdings bereits ein komplexes Unterfangen darstellt, ist einer
            adäquaten, zeitgleichen Beobachtung als Lehrperson kaum gerecht zu
            werden. Daher ist die Hinzunahme weiterer Perspektiven auf den
            Unterricht unabdingbar, um hinreichend differenzierte Hinweise zur
            Unterrichtsentwicklung zu erhalten.
          </p>
          <p>
            Genau hier setzt FormATool an und soll Lehrpersonen den
            systematischen Vergleich mehrerer Perspektiven auf den eigenen
            Unterricht erleichtern. Neben der reflexiven Selbsteinschätzung des
            eigenen unterrichtlichen Handelns haben Lehrpersonen die
            Möglichkeit, Rückmeldungen zum Unterricht von Schüler*innen und/oder
            Beobachter*innen selbstgesteuert heranzuziehen. Durch die parallele
            Formulierung der Fragen und die automatisierte Auswertung gelingt
            der Vergleich der Perspektiven spielend. Dabei ist wichtig
            festzuhalten, dass es keine «richtige» Perspektive gibt für die
            Erfassung von Unterrichtsqualität. Jede Perspektive geht mit ihren
            eigenen Vor- und Nachteilen einher. Die Kombination dieser
            verschiedenen Perspektiven ermöglicht, spannende Hinweise für die
            weitere Unterrichtsgestaltung zu erhalten.
          </p>
        </>
      ),
    },
    {
      title: "Was ist kognitive Aktivierung?",
      text: (
        <>
          <p>
            Kognitive Aktivierung beschreibt die Anregung von Lernenden „zum
            vertieften Nachdenken und zu einer elaborierten Auseinandersetzung“
            (Lipowsky, 2020, S. 92) mit dem Lerngegenstand.
          </p>
          <p>
            Lehrpersonen können eine solche Form der Anregung über das
            Lehr-Lernangebot anstossen. In der Unterrichtsforschung wird davon
            ausgegangen, dass der kognitive Anforderungsgehalt des Unterrichts
            entscheidend für den Auf- und Ausbau von langfristigen und
            vernetzten Wissensstrukturen ist. Dazu ist Unterricht geeignet, der
            anspruchsvolle Aufgaben stellt, die sich auf verschiedenen Wegen
            bearbeiten lassen und über die Anwendung von Routinen hinausgehen.
            Neben der Aufgabenstellung kann die Lehrperson durch entsprechende
            Lernbegleitung der Schüler*innen die kognitive Aktivierung fördern.
            Anregende und spannende Fragen fordern die Schüler*innen heraus,
            sich kritisch mit den Inhalten auseinanderzusetzen, ihre
            Vorgehensweisen zu begründen oder neue Inhalte aktiv mit ihrem
            Vorwissen zu verknüpfen.
          </p>
          <p>
            Eine solche Umsetzung von kognitiv aktivierenden Lerngelegenheiten
            wird oft als herausfordernd thematisiert. Der Forschungsstand
            liefert zahlreiche Merkmale, Materialien, Techniken und Methoden,
            die helfen sollen, kognitiv aktivierende Lehr-Lernprozesse
            umzusetzen. Es fehlt Lehrpersonen allerdings oftmals an Einblicken,
            ob entsprechend geschaffene Lerngelegenheiten kognitiv aktivierend
            sind. Hier setzt das FormATool-Vorhaben an und möchte eine
            webbasierte Lösung für Lehrpersonen bieten, um formative
            Rückmeldungen der Lernenden mit eigenen Einschätzungen zur
            kognitiven Aktivierung im Unterricht abzugleichen.
          </p>
          <p className="text-xs">
            Lipowsky, F. (2020). Unterricht. In E. Wild & J. Möller (Hrsg.),
            <span className="whitespace-normal italic">
              {" Pädagogische Psychologie"}
            </span>
            , S. 69–118. Springer.
            <a
              href="
              https://doi.org/10.1007/978-3-662-61403-7_4"
              target="_blank"
              rel="noreferrer"
              className="whitespace-normal text-[#0028A5]"
            >
              https://doi.org/10.1007/978-3-662-61403-7_4
            </a>
          </p>
        </>
      ),
    },
    {
      title: "Von wem kann FormATool verwendet werden?",
      text: (
        <>
          <p>
            FormATool kann von Lehrpersonen freiwillig eingesetzt werden. Nach
            Registrierung auf der Webseite können auf den individuellen Bedarf
            zugeschnittene Umfragen erstellt und durchgeführt werden.
          </p>
        </>
      ),
    },
    {
      title: "Von wem können die Umfragen ausgefüllt werden?",
      text: (
        <>
          <p>
            Lehrpersonen können entscheiden, welche Perspektiven Sie zu Ihrer
            Selbsteinschätzung hinzunehmen wollen. Sofern eine Fremdeinschätzung
            erwünscht ist, können die Perspektiven der Schüler*innen und
            Beobachter*innen einbezogen werden. Schüler*innen können ab der 4.
            Primarklasse befragt werden. Beobachter*innen können Schulleitungen
            im Rahmen von regulären Unterrichtsbesuchen, andere Lehrpersonen in
            kollegialen Hospitationen oder von der Lehrperson gewählte externe
            Personen sein.
          </p>
        </>
      ),
    },
    {
      title:
        "In welchen Unterrichtslektionen kann FormATool eingesetzt werden?",
      text: (
        <>
          <p>
            Lehrpersonen entscheiden, zu welcher Unterrichtsstunde sie sich
            Rückmeldung wünschen. Es kann frei gewählt werden, welche Klassen
            näher in den Blick genommen werden. Dabei kann FormATool in mehreren
            Klassen zeitgleich oder mit derselben Klasse mehrmalig eingesetzt
            werden, ganz nach individuellem Bedarf. Die Fragen sind so
            konzipiert, dass FormATool in verschiedenen Fächern und für
            verschiedene Zielstufen verwendet werden kann.
          </p>
        </>
      ),
    },
    {
      title: "Von wem wurde FormATool entwickelt?",
      text: (
        <>
          <p>
            FormATool wurde von einem interdisziplinären Team am Institut für
            Erziehungswissenschaft der Universität Zürich entwickelt, um
            Unterrichtsentwicklungsprozesse mit Blick auf kognitive Aktivierung
            anzustossen. Um die Forschung zur kognitiven Aktivierung weiter
            voranzutreiben und die Plattform weiter zu optimieren, werden die
            Nutzungsdaten vertraulich erfasst, anonymisiert aufbereitet und
            ausschliesslich für diese Zwecke verwendet. Von den Antworten sind
            keine Rückschlüsse auf die Angaben einzelner Personen möglich.
            Änderungsvorschläge, Anregungen und sonstiges Feedback nehmen wir
            gerne unter folgender Adresse entgegen:
            <a
              href="mailto:formatool@ife.uzh.ch"
              target="_blank"
              rel="noreferrer"
              className="whitespace-normal text-[#0028A5]"
            >
              {"formatool@ife.uzh.ch"}
            </a>
          </p>
        </>
      ),
    },
    {
      title: "Wie kann FormATool zitiert werden?",
      text: (
        <>
          <p>
            FormATool kann für wissenschaftliche Zwecke wie folgt zitiert
            werden: <Citation />
          </p>
        </>
      ),
    },
  ];

  return (
    <>
      <CustomHelmet pageTitle="Über" />
      <Breadcrumbs
        listItems={[
          { name: "Home", url: "/" },
          { name: "Über FormATool", url: "/uber" },
        ]}
      />
      <BaseContainer>
        <Intro title="Über FormATool">
          <span>
            Wir freuen uns über Ihr Interesse an FormATool. Auf dieser Seite
            finden Sie nähere Informationen zu FormATool. Sollten wir nicht alle
            Ihre Fragen beantwortet haben, nehmen Sie doch gerne{" "}
            <a
              href="mailto:formatool@ife.uzh.ch"
              target="_blank"
              rel="noreferrer"
              className="whitespace-normal text-[#0028A5]"
            >
              Kontakt
            </a>{" "}
            mit uns auf. Wir freuen uns, wenn wir Ihnen helfen können.
          </span>
        </Intro>
        <ContentArea>
          <div>
            <ul className="NewsList--list">
              {klappen_texte.map((element) => (
                <li key={element.title}>
                  <SlideDown title={element.title} content={element.text} />
                </li>
              ))}
            </ul>
          </div>
        </ContentArea>
      </BaseContainer>
    </>
  );
}
